import { niveisAlerta } from 'utils/alertas';
import { parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

// Base compartilhada das seis telas do menu Cobranca. Todas batem no MESMO
// endpoint (/cobrancas) e recebem o MESMO retorno; o que muda entre elas e o
// filtro. Por isso a chamada, o mapeamento das linhas, os indicadores e a
// classificacao da esteira vivem aqui, e nao copiados em cada tela.

// --- Filtros enviados ao endpoint ---------------------------------------

// A esteira e recortada NA QUERY, sobre `maior_atraso` — campo do CONTRATO,
// calculado no backend. O front nao reclassifica nada depois de receber.
//
// Filtrar por `titulos.dias_vencido` nao serve aqui: a condicao seria avaliada
// titulo a titulo, e bastaria uma parcela cair na faixa para o contrato
// inteiro voltar. Um contrato com parcelas de 89, 59 e 28 dias aparecia nas
// tres telas ao mesmo tempo. Com `maior_atraso` a comparacao e do contrato, e
// cada um cai em um degrau so.
//
// Contrato encaminhado para a esteira juridica tambem sai dos degraus de
// atraso: uma vez em `notificacao` ou `execucao` a regua para de disparar, e
// ele deixou de ser caso de cobranca amigavel. Os contadores do menu tambem
// nao contam os encaminhados, entao a listagem precisa fazer o mesmo, senao o
// badge diz 5 e a tela mostra 7.
// `$notIn` é indexado (`[0]`, `[1]`): repetir a mesma chave sem índice vira
// array no `qs` e o Strapi rejeita.
const notStatus = (...values) =>
  values
    .map((value, index) => `&filters[status][$notIn][${index}]=${value}`)
    .join('');

// `excludeRenegotiating` tira os contratos com status `renegociar`. Vale para
// os degraus de cobranca amigavel; a Renegociacao desliga, porque e
// justamente a tela de quem esta nesse status.
const delayRange = (from, to, { excludeRenegotiating = true } = {}) =>
  `?filters[maior_atraso][$gte]=${from}` +
  (to ? `&filters[maior_atraso][$lte]=${to}` : '') +
  (excludeRenegotiating ? '&filters[$not][status]=renegociar' : '') +
  notStatus('notificacao', 'execucao');

export const BILLING_QUERIES = {
  // Sem recorte nenhum: todos os contratos, todos os status.
  all: '',
  // Carteira: o operador alterna entre os titulos em aberto (padrao) e os
  // baixados, e cada visao reconsulta o endpoint. O filtro por relacao escolhe
  // os CONTRATOS que tem ao menos um titulo naquela situacao; o recorte das
  // parcelas em si e feito no buildRows (`walletView`), porque o contrato vem
  // com o array `titulos` inteiro.
  walletOpen: '?filters[titulos][status][$ne]=baixado',
  walletSettled: '?filters[titulos][status][$eq]=baixado',
  cobranca: delayRange(1, 30),
  recuperacao: delayRange(31, 60),
  // Renegociacao nao tem teto: fica com todo contrato acima de 60 dias ate o
  // operador encaminha-lo para a Notificacao. Nada sai daqui sozinho por
  // atraso. Diferente dos degraus anteriores, mantem os contratos com status
  // `renegociar`: eles estao em renegociacao, e esta e a tela deles.
  renegociacao: delayRange(61, undefined, { excludeRenegotiating: false }),
  // Regua: tela fora do menu (escondida a pedido do time), com a mesma faixa
  // que a Cobranca assumiu. A rota continua respondendo.
  regua: delayRange(1, 30),

  // Notificacao e Execucao sao recortadas por STATUS, e nao por atraso: o
  // contrato so chega nelas porque alguem o encaminhou, e continua la por
  // quantos dias de atraso venha a acumular.
  notificacao: '?filters[status][$eq]=notificacao',

  execucao: '?filters[status][$eq]=execucao',
};

// --- Consolidacao por contrato -------------------------------------------

// Pior atraso de cada contrato, medido sobre TODAS as linhas dele — inclusive
// as que a busca textual esconderia. So alimenta o filtro de/ate que o
// operador digita no cabecalho, que e refinamento sobre o que ja veio; o
// recorte da tela em si vem da query.
export const buildWorstDelay = (rows) => {
  const worst = new Map();
  (rows || []).forEach((row) => {
    // O contrato ja traz o proprio maior atraso — o mesmo campo que recorta a
    // esteira na query. A conta local so cobre o caso de ele nao vir.
    if (Number.isFinite(row?.maior_atraso)) {
      worst.set(row?.id, row.maior_atraso);
      return;
    }
    const atual = worst.get(row?.id) || 0;
    worst.set(row?.id, Math.max(atual, row?.dias_atraso || 0));
  });
  return worst;
};

// --- Alertas -------------------------------------------------------------

// Um alerta vale para o contrato OU para o cliente — nesse segundo caso ele
// aparece em todos os contratos da pessoa, entao sao dois indices.
export const buildAlertIndex = (alertas) => {
  const byContract = new Map();
  const byCustomer = new Map();

  (alertas || []).forEach((item) => {
    const id = item?.cobranca?.id || item?.cliente?.id;
    if (!id) {
      return;
    }
    const index = item?.cobranca?.id ? byContract : byCustomer;
    index.set(id, [...(index.get(id) || []), item]);
  });

  return { byContract, byCustomer };
};

// Regra unica do alerta critico: alimenta a contagem do card e o filtro que
// ele liga, para os dois nunca divergirem.
export const hasCriticalAlert = (row) =>
  (row?.alertas || []).some((item) => item?.nivel === 'critico');

// --- Consolidacao da linha do grupo --------------------------------------

// O grid traz uma linha por titulo, entao o mesmo contrato se repete. Na linha
// agrupada, as colunas que variam entre as parcelas mostram o consolidado.
export const sumBy = (rows, field) =>
  (rows || []).reduce((total, row) => total + (row?.[field] || 0), 0);

export const maxBy = (rows, field) =>
  (rows || []).reduce((maior, row) => Math.max(maior, row?.[field] || 0), 0);

// A lista ja vem ordenada por vencimento, mas nao da para contar com isso
// depois que o operador ordena o grid por outra coluna.
export const oldestDate = (rows, field) =>
  (rows || [])
    .map((row) => row?.[field])
    .filter(Boolean)
    .sort((a, b) => new Date(a) - new Date(b))[0];

// --- Mapeamento das linhas ----------------------------------------------

const pick = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const toNumber = (value) => {
  const parsed =
    typeof value === 'string' ? Number(value.replace(',', '.')) : value;
  return Number.isFinite(parsed) ? parsed : null;
};

// Valor vazio vira null, e nao 'R$ 0' nem '-': null vai para o fim na
// ordenacao e renderiza em branco, que e o comportamento certo para campo que
// a API ainda nao manda.
const currency = (value) => {
  const numero = toNumber(value);
  return numero === null ? null : parseCurrency(numero);
};

// As duas visoes da Carteira, escolhidas pelo operador no cabecalho.
export const WALLET_VIEWS = {
  open: 'open', // titulos em aberto: vencidos e a vencer
  settled: 'settled', // titulos baixados
};

// Campos do contrato — repetidos em todas as linhas dele.
const contractFields = (contrato, alertas) => {
  const titulos = contrato?.titulos || [];
  const emAberto = titulos.filter((titulo) => titulo?.status !== 'baixado');

  // Derivados dos titulos, usados so enquanto a API nao mandar o campo
  // proprio. Servem para a coluna nao nascer vazia; assim que o backend
  // entregar o valor, o pick abaixo passa a preferir o dele.
  const totalContrato = titulos.length
    ? titulos.reduce(
        (total, titulo) => total + (toNumber(titulo?.valor) || 0),
        0
      )
    : null;

  // Saldo do contrato: o `valor_saldo` do backend menos o `valor` dos titulos
  // baixados. A conta usa todos os titulos, e nao as linhas: nenhuma tela
  // mostra os baixados como linha.
  const valorSaldo = toNumber(contrato?.valor_saldo);
  const totalBaixado = titulos
    .filter((titulo) => titulo?.status === 'baixado')
    .reduce((total, titulo) => total + (toNumber(titulo?.valor) || 0), 0);
  // Saldo zero e contrato quitado: nao ha o que descontar, e subtrair os
  // baixados deixaria o saldo negativo.

  return {
    id: contrato?.id,
    contrato: contrato?.contrato,
    // Renegociacao (ver components/Billing/renegotiationTag.js). A cobranca
    // nova traz a original em `cobranca`; a original traz as geradas por ela
    // em `renegociacoes`. Uma mesma cobranca pode ter os dois.
    renegotiationOrigin: contrato?.cobranca?.id
      ? { id: contrato.cobranca.id, contrato: contrato.cobranca.contrato }
      : null,
    renegotiations: Array.isArray(contrato?.renegociacoes)
      ? contrato.renegociacoes
      : [],
    // Parcelas baixadas por renegociacao: com titulo ainda em aberto, a
    // renegociacao foi parcial.
    hasRenegotiatedTitles: titulos.some(
      (titulo) => titulo?.tipo_baixa === 'renegociado'
    ),
    hasOpenTitles: emAberto.length > 0,
    data_emissao: contrato?.data_emissao,
    ultima_tentativa: contrato?.ultima_tentativa,
    // Maior atraso do contrato, calculado no backend. E por ele que a
    // esteira e recortada na query.
    maior_atraso: contrato?.maior_atraso,
    // Saldo do CONTRATO, ja descontados os baixados. A coluna Saldo e so do
    // contrato: as parcelas nao tem saldo proprio (ver `saldo` em
    // components/Billing/columns.js).
    saldo: currency(valorSaldo === null ? null : valorSaldo),
    date: contrato?.createdAt,
    formatedDate: formatadores.TIMESTAMP_TO_BR(contrato?.createdAt),
    cliente: contrato?.cliente,
    customer: normalizeName(contrato?.cliente?.nome_completo) || '',
    telefone: contrato?.cliente?.telefone_celular,
    distributor: normalizeName(contrato?.distribuidor?.trade_name) || '',
    distributorId: contrato?.distribuidor?.id,
    // Desconto maximo (%) que a distribuidora aceita na baixa manual. A aba
    // Baixa do Historico usa so para mostrar o minimo: quem valida e o backend.
    discountLimit:
      contrato?.distribuidor?.cobranca_regras_gerais?.perc_limite_desconto ??
      null,
    integrator:
      normalizeName(contrato?.proposta_financeira?.integrador?.nome_fantasia) ||
      '',
    banco: normalizeName(contrato?.banco?.nome_fantasia),
    bureau_perfil: contrato?.banking?.bureau_perfil || '-',
    parcelas_origem: contrato?.parcelas_origem,
    renegociacao_taxa: contrato?.renegociacao_taxa,
    titulos,
    status: contrato?.status,

    // Campos que o modelo ainda nao tem. Ficam tolerantes a undefined e
    // aceitam mais de um caminho, para o front nao precisar mudar quando o
    // backend escolher onde por cada um.
    ope: pick(contrato?.ope, contrato?.operacao),
    seller:
      normalizeName(
        pick(
          contrato?.seller,
          contrato?.vendedor?.nome,
          contrato?.proposta_financeira?.vendedor?.nome
        )
      ) || '',
    seller_phone: pick(
      contrato?.seller_phone,
      contrato?.vendedor?.telefone_celular,
      contrato?.proposta_financeira?.vendedor?.telefone_celular
    ),
    score: pick(contrato?.score, contrato?.banking?.score),
    // CON e o valor total do contrato.
    con: currency(pick(contrato?.con, contrato?.valor_total, totalContrato)),
    con_num: pick(
      toNumber(contrato?.con),
      toNumber(contrato?.valor_total),
      totalContrato
    ),

    alertas,
    // A busca global compara o termo com todos os valores da linha; array de
    // objeto vira "[object Object]", entao as mensagens vao tambem em texto
    // plano para dar para achar o contrato pelo teor do alerta.
    alerta_mensagens: (alertas || [])
      .map((alerta) => alerta?.mensagem || '')
      .join(' '),

    _prazo_contrato: emAberto.length || null,
  };
};

// Status da cobranca gerada por uma renegociacao que foi desfeita, pelo
// operador ou pelo estorno automatico da entrada. Todos os titulos dela ficam
// baixados com `tipo_baixa: cancelado`, e ela nao aceita baixa nem nova
// renegociacao.
export const CANCELLED_STATUS = 'cancelado';

// Rotulo da parcela para exibicao. O titulo de entrada da renegociacao vem com
// `parcela: 0` e `entrada: true`, e aparece como "Entrada" em vez de "0".
export const installmentLabel = (titulo) =>
  titulo?.entrada === true ? 'Entrada' : titulo?.parcela;

// Campos do titulo — o que varia de uma parcela para outra.
const installmentFields = (titulo, contrato, base) => {
  const valorNum = toNumber(titulo?.valor);
  const pmtTotal =
    (toNumber(titulo?.valor_juros_remun) || 0) +
    (toNumber(titulo?.capital) || 0) +
    (toNumber(titulo?.valor_juros_mora) || 0) +
    (toNumber(titulo?.valor_multa) || 0) +
    (toNumber(titulo?.valor_iof_juros) || 0);

  return {
    tituloId: titulo?.id,
    parcela: titulo?.parcela,
    // Titulo de entrada da renegociacao (parcela 0). Ver `installmentLabel`.
    entrada: titulo?.entrada === true,
    // `renegociado` marca a parcela baixada por renegociacao, que na visao
    // Baixados da Carteira nao pode passar por paga.
    tipo_baixa: titulo?.tipo_baixa ?? null,
    // Mora dispensada na renegociacao: os campos de mora continuam com o que
    // era devido, e este marcador diz que ela nao foi cobrada.
    mora_dispensada: titulo?.mora_dispensada === true,
    data_vencimento: titulo?.data_vencimento,
    // Atraso DESTA parcela. O maior atraso do contrato vem separado, em
    // `maior_atraso`: e ele que recorta a esteira e que a linha do grupo
    // mostra. Aqui tem que ser o do titulo, senao as parcelas expandidas
    // repetem todas o mesmo numero.
    dias_atraso: titulo?.dias_vencido ?? null,
    titulo_status: titulo?.status,
    overdue: titulo?.status === 'vencido',

    // Os valores circulam formatados ('R$ 1.234,56') porque a busca global
    // compara texto; o cru fica ao lado porque a linha do contrato precisa
    // soma-los.
    valor_parcelas: currency(valorNum),
    valor_parcela_num: valorNum || 0,
    // Coluna Valor: o `valor` DESTE titulo. O valor do contrato e a soma
    // deles, feita na linha do grupo (ver `value` em components/Billing/columns.js).
    value: currency(valorNum),
    value_num: valorNum,
    pmt_total: currency(pmtTotal),
    pmt_total_num: pmtTotal,

    valor_liquido: currency(
      pick(titulo?.valor_liquido, contrato?.valor_liquido)
    ),
    valor_liquido_num: pick(
      toNumber(titulo?.valor_liquido),
      toNumber(contrato?.valor_liquido)
    ),
    prazo: pick(titulo?.prazo, contrato?.prazo, base?._prazo_contrato),
  };
};

// Achata o retorno em uma linha por titulo. `onlyOverdue` e o que separa a
// esteira (so parcelas vencidas) da carteira (todas).
//
// O filtro por relacao no Strapi escolhe quais CONTRATOS voltam, mas nao
// recorta os titulos populados: o contrato vem com o array inteiro. Por isso o
// recorte da parcela acontece aqui, e nao na query.
//
// `walletView` e o recorte da carteira (ver WALLET_VIEWS): em aberto mostra
// todo titulo nao baixado, vencido ou a vencer; baixados mostra so os pagos.
// O saldo do contrato nao depende da visao: e calculado sobre todos os
// titulos.
export const buildRows = (
  registers,
  alertIndex,
  { onlyOverdue = true, walletView = null } = {}
) => {
  const index = alertIndex || { byContract: new Map(), byCustomer: new Map() };
  const rows = [];

  (registers || []).forEach((contrato) => {
    const alertas = [
      ...(index.byContract.get(contrato?.id) || []),
      ...(index.byCustomer.get(contrato?.cliente?.id) || []),
    ];
    // Cobranca cancelada so aparece na visao Baixados da Carteira, onde estao
    // os titulos dela. Na esteira e em Em aberto ela ja nao deveria voltar
    // (titulos todos baixados, sem atraso); o descarte e so uma garantia.
    if (
      contrato?.status === CANCELLED_STATUS &&
      walletView !== WALLET_VIEWS.settled
    ) {
      return;
    }
    const base = contractFields(contrato, alertas);
    const titulos = (contrato?.titulos || []).filter((titulo) => {
      if (onlyOverdue) {
        return titulo?.status === 'vencido';
      }
      if (walletView === WALLET_VIEWS.open) {
        return titulo?.status !== 'baixado';
      }
      if (walletView === WALLET_VIEWS.settled) {
        return titulo?.status === 'baixado';
      }
      return true;
    });

    // Na carteira, contrato sem nenhum titulo na visao escolhida fica de fora
    // (o quitado nao aparece em "Em aberto", o que nunca pagou nada nao
    // aparece em "Baixados"). Sem recorte, o contrato sem titulo continua
    // sendo uma linha.
    if (!titulos.length && walletView) {
      return;
    }

    if (!titulos.length) {
      rows.push({ ...base, ...installmentFields(null, contrato, base) });
      return;
    }
    titulos.forEach((titulo) => {
      rows.push({ ...base, ...installmentFields(titulo, contrato, base) });
    });
  });

  return rows.sort(
    (a, b) =>
      new Date(a?.data_vencimento || 0) - new Date(b?.data_vencimento || 0)
  );
};

// --- Cards de resumo -----------------------------------------------------

// Os indicadores refletem o que esta filtrado na tela — busca, degrau e faixa
// de atraso incluidos —, nao a carteira inteira.
export const buildIndicators = (rows) => {
  const clientes = new Set();
  // Contratos com alerta critico: contados por contrato, nao por parcela,
  // senao o mesmo alerta seria somado uma vez para cada titulo.
  const criticos = new Set();
  let valor = 0;
  let maiorAtraso = 0;

  (rows || []).forEach((row) => {
    // Cobranca sem cliente vinculado nao entra na contagem de pessoas.
    if (row?.cliente?.id) {
      clientes.add(row.cliente.id);
    }
    if (hasCriticalAlert(row)) {
      criticos.add(row?.id);
    }
    valor += row?.valor_parcela_num || 0;
    maiorAtraso = Math.max(maiorAtraso, row?.dias_atraso || 0);
  });

  return {
    valor,
    titulos: (rows || []).length,
    clientes: clientes.size,
    criticos: criticos.size,
    maiorAtraso,
  };
};

export const buildCards = (
  indicators,
  { onToggleCritical, criticalActive = false, overdueOnly = true } = {}
) => [
  {
    label: 'Valor total dos títulos',
    valor: parseCurrency(indicators?.valor || 0),
    rodape: overdueOnly
      ? 'Soma das parcelas vencidas'
      : 'Soma das parcelas listadas',
    cor: '#00A82A',
  },
  {
    label: 'Títulos',
    valor: indicators?.titulos || 0,
    rodape: overdueOnly ? 'Parcelas vencidas' : 'Parcelas na carteira',
    cor: '#FF9500',
  },
  {
    label: 'Clientes',
    valor: indicators?.clientes || 0,
    rodape: 'Pessoas a cobrar',
    cor: '#7B44F2',
  },
  {
    label: 'Com alerta crítico',
    valor: indicators?.criticos || 0,
    rodape: 'Contratos com alerta crítico',
    cor: niveisAlerta.critico.cor,
    // Sem nenhum critico nao ha o que filtrar; ligado, o clique desliga.
    onClick:
      onToggleCritical && (indicators?.criticos || criticalActive)
        ? onToggleCritical
        : null,
    ativo: criticalActive,
  },
  {
    label: 'Maior atraso',
    valor: `${indicators?.maiorAtraso || 0} dias`,
    rodape: 'Parcela mais antiga em aberto',
    cor: '#2d3748',
  },
];
